/**
 * EPIC-DM01-F: Demo Activation Page (Redesigned)
 * Marketing/conversion page for demo user activation
 *
 * Supports two entry modes:
 *  - ?token=xxx  → marketing page + password form (from email link)
 *  - ?session=xxx → marketing page + "check your email" prompt (from results CTA)
 *  - no params    → marketing page only (generic)
 *
 * Route: /activate/demo
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  LogIn,
  Star,
  Users,
  XCircle,
  Zap,
  BarChart3,
  Target,
  Dumbbell,
} from 'lucide-react';
import { useDemoActivation } from '@/hooks/useDemoActivation';
import { DemoButton, DemoCard } from '@/components/demo/ui';
import { ActivationForm } from '@/components/demo/ActivationForm';

const FEATURES = [
  {
    icon: Target,
    title: 'AI Swing Analysis',
    description: 'Upload any swing video and get detailed fault detection with root-cause analysis.',
  },
  {
    icon: Dumbbell,
    title: 'Personalized Drills',
    description: 'Receive drills targeted to your specific swing faults with step-by-step instructions.',
  },
  {
    icon: BarChart3,
    title: 'Progress Tracking',
    description: 'Track your improvement over time with swing-by-swing comparisons.',
  },
  {
    icon: Zap,
    title: 'Swing Thoughts',
    description: 'Get simple, memorable cues to focus on during your rounds and practice.',
  },
  {
    icon: Users,
    title: 'Community Access',
    description: 'Join golfers working to break through plateaus with data-driven coaching.',
  },
];

const TESTIMONIAL = {
  quote: "I dropped 4 strokes in my first month. The drills were so specific to my swing that I saw results after the first range session.",
  name: 'Mark T.',
  handicap: '14 → 10 handicap',
  stars: 5,
};

export default function DemoActivation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const sessionId = searchParams.get('session');

  const {
    state,
    error,
    tokenData,
    redirectUrl,
    isValidating,
    isValid,
    isInvalid,
    isCompleting,
    isCompleted,
    startActivation,
    completeActivation,
    reset,
  } = useDemoActivation();

  const [activationError, setActivationError] = useState<Error | null>(null);

  useEffect(() => {
    if (!token) return;
    startActivation(token);
  }, [token, startActivation]);

  const handlePasswordSubmit = async (password: string) => {
    if (!token) return;
    setActivationError(null);
    const result = await completeActivation(token, password);
    if (!result.ok) {
      setActivationError(new Error(
        result.validation_errors?.join('. ') || result.message || 'Activation failed'
      ));
    }
  };

  const handleGoToDashboard = () => {
    if (redirectUrl) {
      if (redirectUrl.startsWith('http')) {
        window.location.href = redirectUrl;
      } else {
        navigate(redirectUrl);
      }
    } else {
      navigate('/auth?activated=true');
    }
  };

  const prospectName = tokenData?.prospect_name;

  // ── Loading state ──────────────────────────────────────────────
  if (token && isValidating) {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-demo-green" />
          <p className="text-lg text-demo-text-muted">Validating your activation link...</p>
        </div>
      </div>
    );
  }

  // ── Invalid/expired token ──────────────────────────────────────
  if (token && isInvalid) {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <DemoCard className="max-w-md w-full">
          <div className="p-8 text-center">
            <XCircle className="h-16 w-16 mx-auto mb-4 text-demo-error" />
            <h2 className="text-[length:var(--demo-font-2xl)] font-bold mb-2 text-demo-text">Link Expired</h2>
            <p className="text-demo-text-muted mb-6">
              {error?.message || 'This activation link is invalid or has expired. Please request a new one.'}
            </p>
            <div className="space-y-3">
              <DemoButton variant="primary" fullWidth onClick={() => navigate('/auth')}>
                Go to Login
              </DemoButton>
              <DemoButton variant="ghost" fullWidth onClick={reset}>
                Try Again
              </DemoButton>
            </div>
          </div>
        </DemoCard>
      </div>
    );
  }

  // ── Already activated ──────────────────────────────────────────
  if (isValid && tokenData?.next_step === 'already_activated') {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <DemoCard className="max-w-md w-full">
          <div className="p-8 text-center">
            <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-demo-green" />
            <h2 className="text-[length:var(--demo-font-2xl)] font-bold mb-2 text-demo-text">Already Activated</h2>
            <p className="text-demo-text-muted mb-6">
              Your account is ready. Log in to access your swing analysis and training.
            </p>
            <DemoButton variant="primary" fullWidth onClick={() => navigate('/auth')}>
              <LogIn className="mr-2 h-5 w-5" />
              Go to Login
            </DemoButton>
          </div>
        </DemoCard>
      </div>
    );
  }

  // ── Activation completed ───────────────────────────────────────
  if (isCompleted) {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <DemoCard className="max-w-md w-full">
          <div className="p-8 text-center">
            <div className="mx-auto mb-6 w-20 h-20 bg-demo-green-light rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-demo-green-dark" />
            </div>
            <h2 className="text-[length:var(--demo-font-2xl)] font-bold mb-2 text-demo-text">Account Activated!</h2>
            <p className="text-demo-text-muted mb-8">
              Your 7-day free trial has started. You now have full access to personalized swing coaching.
            </p>

            <div className="grid grid-cols-3 gap-4 mb-8 text-center">
              <div>
                <Target className="h-6 w-6 mx-auto mb-2 text-demo-green" />
                <p className="text-xs text-demo-text-muted">Unlimited Analysis</p>
              </div>
              <div>
                <Dumbbell className="h-6 w-6 mx-auto mb-2 text-demo-green" />
                <p className="text-xs text-demo-text-muted">Custom Drills</p>
              </div>
              <div>
                <BarChart3 className="h-6 w-6 mx-auto mb-2 text-demo-green" />
                <p className="text-xs text-demo-text-muted">Progress Tracking</p>
              </div>
            </div>

            <DemoButton variant="primary" size="lg" fullWidth onClick={handleGoToDashboard}>
              Go to Dashboard
            </DemoButton>
          </div>
        </DemoCard>
      </div>
    );
  }

  // ── Login redirect ─────────────────────────────────────────────
  if (isValid && tokenData?.next_step === 'login') {
    return (
      <div className="demo-mode min-h-screen bg-demo-surface flex items-center justify-center p-4">
        <DemoCard className="max-w-md w-full">
          <div className="p-8 text-center">
            <h2 className="text-[length:var(--demo-font-2xl)] font-bold mb-2 text-demo-text">Welcome Back!</h2>
            <p className="text-demo-text-muted mb-6">
              Your account is already set up. Log in to continue.
            </p>
            <div className="bg-demo-surface-alt rounded-lg p-4 text-center mb-6">
              <p className="text-sm text-demo-text-muted">Account</p>
              <p className="font-medium text-demo-text">{tokenData.masked_email}</p>
            </div>
            <DemoButton variant="primary" size="lg" fullWidth onClick={() => navigate('/auth')}>
              <LogIn className="mr-2 h-5 w-5" />
              Go to Login
            </DemoButton>
          </div>
        </DemoCard>
      </div>
    );
  }

  // ── Main marketing/conversion page ─────────────────────────────
  const showPasswordForm = isValid && tokenData?.next_step === 'set_password';

  return (
    <div className="demo-mode min-h-screen bg-demo-surface">
      <div className="max-w-lg mx-auto px-4 py-8 pb-16">

        {/* Brand header */}
        <div className="text-center mb-6">
          <p className="text-xs font-bold tracking-widest uppercase text-demo-green mb-2">
            No Doubles Golf Co
          </p>
        </div>

        {/* Hero heading */}
        <div className="text-center mb-8">
          <h1 className="text-[length:var(--demo-font-4xl)] font-bold text-demo-text leading-tight mb-3">
            {prospectName
              ? `Ready to Fix Your Swing, ${prospectName}?`
              : 'Ready to Fix Your Swing?'}
          </h1>
          <p className="text-[length:var(--demo-font-base)] text-demo-text-muted">
            Your personalized analysis is just the beginning. Unlock the full coaching experience.
          </p>
        </div>

        {/* 3 benefit badges */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="bg-demo-green-light rounded-xl p-4 text-center">
            <Clock className="h-6 w-6 mx-auto mb-2 text-demo-green-dark" />
            <p className="text-sm font-semibold text-demo-green-darkest">2-4 Week</p>
            <p className="text-xs text-demo-green-dark">Fix Timeline</p>
          </div>
          <div className="bg-demo-gold-light rounded-xl p-4 text-center">
            <Zap className="h-6 w-6 mx-auto mb-2 text-demo-gold-dark" />
            <p className="text-sm font-semibold text-demo-gold-darkest">15 Min</p>
            <p className="text-xs text-demo-gold-dark">3x Per Week</p>
          </div>
          <div className="bg-demo-green-light rounded-xl p-4 text-center">
            <Users className="h-6 w-6 mx-auto mb-2 text-demo-green-dark" />
            <p className="text-sm font-semibold text-demo-green-darkest">2,500+</p>
            <p className="text-xs text-demo-green-dark">Active Golfers</p>
          </div>
        </div>

        {/* Activate callout */}
        <DemoCard variant="highlight" className="mb-8">
          <div className="text-center py-2">
            <h3 className="text-[length:var(--demo-font-xl)] font-bold text-demo-green-darkest mb-2">
              Activate Your Account
            </h3>
            <p className="text-sm text-demo-text-muted">
              Set your password below to get started with personalized coaching.
            </p>
          </div>
        </DemoCard>

        {/* Password form (only when token is validated) */}
        {showPasswordForm && (
          <div className="mb-8">
            <ActivationForm
              maskedEmail={tokenData!.masked_email || '***@***'}
              prospectName={tokenData!.prospect_name}
              onSubmit={handlePasswordSubmit}
              isSubmitting={isCompleting}
              error={activationError}
            />
          </div>
        )}

        {/* If no token but has session — prompt to check email */}
        {!token && sessionId && (
          <DemoCard className="mb-8">
            <div className="text-center py-4">
              <AlertCircle className="h-10 w-10 mx-auto mb-3 text-demo-gold" />
              <h3 className="text-lg font-semibold text-demo-text mb-2">Check Your Email</h3>
              <p className="text-sm text-demo-text-muted mb-4">
                We sent an activation link to your email. Click the link in the email to set your password and start your trial.
              </p>
              <DemoButton variant="secondary" onClick={() => navigate(`/demo/results/${sessionId}`)}>
                Back to Results
              </DemoButton>
            </div>
          </DemoCard>
        )}

        {/* If no token and no session — generic CTA */}
        {!token && !sessionId && (
          <DemoCard className="mb-8">
            <div className="text-center py-4">
              <h3 className="text-lg font-semibold text-demo-text mb-2">Get Started</h3>
              <p className="text-sm text-demo-text-muted mb-4">
                Visit a demo event to get your free swing analysis, or log in if you already have an account.
              </p>
              <DemoButton variant="primary" fullWidth onClick={() => navigate('/auth')}>
                <LogIn className="mr-2 h-5 w-5" />
                Go to Login
              </DemoButton>
            </div>
          </DemoCard>
        )}

        {/* What's Included section */}
        <div className="mb-8">
          <h2 className="text-[length:var(--demo-font-xl)] font-bold text-demo-text mb-4 text-center">
            What's Included in Your Trial
          </h2>
          <div className="space-y-3">
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="flex items-start gap-4 bg-white rounded-xl border border-gray-100 p-4 shadow-demo-sm"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-demo-green-light rounded-lg flex items-center justify-center">
                  <feature.icon className="h-5 w-5 text-demo-green-dark" />
                </div>
                <div>
                  <h3 className="font-semibold text-demo-text text-sm">{feature.title}</h3>
                  <p className="text-xs text-demo-text-muted mt-0.5">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <DemoCard className="mb-8">
          <div className="text-center py-2">
            <div className="flex justify-center gap-0.5 mb-3">
              {Array.from({ length: TESTIMONIAL.stars }).map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-demo-gold text-demo-gold" />
              ))}
            </div>
            <p className="text-sm text-demo-text italic mb-3">
              "{TESTIMONIAL.quote}"
            </p>
            <p className="text-sm font-semibold text-demo-text">{TESTIMONIAL.name}</p>
            <p className="text-xs text-demo-text-muted">{TESTIMONIAL.handicap}</p>
          </div>
        </DemoCard>

        {/* Bottom hint */}
        {!showPasswordForm && (
          <div className="text-center">
            <p className="text-xs text-demo-text-muted">
              Check your email for the activation link
            </p>
          </div>
        )}

        {/* Already activated link */}
        <p className="text-center text-sm text-demo-text-muted mt-6">
          Already activated?{' '}
          <button
            className="text-demo-green font-medium underline underline-offset-2"
            onClick={() => navigate('/auth')}
          >
            Log in
          </button>
        </p>
      </div>
    </div>
  );
}
